import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiBook, FiShield, FiCpu, FiFileText, FiCheckCircle,
  FiArrowRight, FiUser, FiHome, FiChevronDown, FiChevronUp, FiDownload
} from 'react-icons/fi';
import ThemeSwitcher from '../components/ThemeSwitcher';

const LandingPage = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      q: "How does college email domain verification work?",
      a: "Every college registered in Notebook has an authorized email domain (e.g. iiitsurat.ac.in). When students or teachers sign up, our system automatically verifies that their email ends with that exact domain before granting access."
    },
    {
      q: "Who can upload notes directly vs submit for review?",
      a: "Faculty teachers and System Administrators can directly upload study materials and past exam papers. Students can submit note requests, which are reviewed and approved by faculty members before publication."
    },
    {
      q: "How does the AI Notes Summarizer work?",
      a: "Our integrated AI service processes text or PDF documents uploaded to the library and extracts key concepts, formulas, and bulleted takeaways in seconds."
    },
    {
      q: "Can I download study materials for offline reading?",
      a: "Yes! All approved notes and past exam papers can be downloaded in PDF or image format with a single click."
    }
  ];

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="landing-page">
      {/* Top Header Navigation */}
      <header className="landing-header">
        <div className="landing-nav-container">
          <a href="#hero" className="landing-brand">
            <FiBook size={24} />
            <span>Notebook</span>
          </a>

          <ul className="landing-nav-links">
            <li><a href="#features">Features</a></li>
            <li><a href="#how-it-works">How It Works</a></li>
            <li><a href="#roles">Roles</a></li>
            <li><a href="#faq">FAQ</a></li>
          </ul>

          <div className="d-flex align-center gap-3">
            <ThemeSwitcher />
            <button
              className="btn btn-outline btn-sm"
              onClick={() => navigate('/login?mode=login')}
            >
              Sign In
            </button>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => navigate('/login?mode=register')}
            >
              Get Started
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section id="hero" className="landing-hero">
        <div className="hero-pill">
          <FiCpu /> AI-Powered Academic Workspace
        </div>
        <h1>
          The Centralized Study Workspace for <span>College Students & Faculty</span>
        </h1>
        <p>
          Discover domain-verified course notes, past exam papers, and instant AI document summaries tailored specifically to your institution.
        </p>

        <div className="hero-cta-group">
          <button
            className="btn btn-primary"
            style={{ height: '48px', padding: '0 28px', fontSize: '15px' }}
            onClick={() => navigate('/login?mode=register')}
          >
            Get Started Free <FiArrowRight />
          </button>
          <button
            className="btn btn-secondary"
            style={{ height: '48px', padding: '0 24px', fontSize: '15px' }}
            onClick={() => navigate('/login?mode=login')}
          >
            Sign In to Portal
          </button>
        </div>

        {/* Feature Cards Visual Teaser */}
        <div className="card" style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'left', padding: '24px' }}>
          <div className="d-flex justify-between align-center mb-3">
            <span className="badge badge-teacher">FACULTY APPROVED</span>
            <span className="text-sm text-secondary">CS201 • Data Structures</span>
          </div>
          <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
            Unit 3: Binary Search Trees & Graph Algorithms Notes
          </h3>
          <p className="text-secondary text-sm mb-3">
            Comprehensive lecture notes covering tree traversals, AVL rotations, Dijkstra shortest path algorithm, and time complexity analyses.
          </p>
          <div className="d-flex justify-between align-center pt-3" style={{ borderTop: '1px solid var(--border-subtle)' }}>
            <span className="text-sm text-muted">Uploaded by Dr. Sharma • IIIT Surat</span>
            <button className="btn btn-sm btn-primary" onClick={() => navigate('/login?mode=register')}>
              <FiDownload /> Download PDF
            </button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="landing-section">
        <div className="section-title-group">
          <h2>Everything You Need to Excel in College</h2>
          <p>Built from the ground up for institution security, peer collaboration, and rapid exam preparation.</p>
        </div>

        <div className="landing-grid-4">
          <div className="landing-feature-card">
            <div className="feature-icon-box">
              <FiShield />
            </div>
            <h3>Domain Verification</h3>
            <p>Registration requires matching your institution's email domain (@iiitsurat.ac.in), ensuring isolated content privacy.</p>
          </div>

          <div className="landing-feature-card">
            <div className="feature-icon-box">
              <FiBook />
            </div>
            <h3>Organized Library</h3>
            <p>Filter notes and past papers by department, semester, subject, and exam type (Midsem, Endsem, Quiz).</p>
          </div>

          <div className="landing-feature-card">
            <div className="feature-icon-box">
              <FiCpu />
            </div>
            <h3>AI Document Summarizer</h3>
            <p>Transform long PDF notes into concise key concepts, formulas, and bulleted takeaways instantly using AI.</p>
          </div>

          <div className="landing-feature-card">
            <div className="feature-icon-box">
              <FiCheckCircle />
            </div>
            <h3>Faculty Review & Approval</h3>
            <p>Student note submissions are reviewed by college teachers before publication to guarantee study quality.</p>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="landing-section" style={{ backgroundColor: 'var(--bg-surface-hover)', borderRadius: 'var(--radius-xl)' }}>
        <div className="section-title-group">
          <h2>How Notebook Works</h2>
          <p>Get started in three simple steps to unlock your institution's repository.</p>
        </div>

        <div className="step-timeline">
          <div className="step-card">
            <div className="step-number">1</div>
            <h3>Register with College Email</h3>
            <p>Sign up selecting your institution and using your assigned college email address to verify domain membership.</p>
          </div>

          <div className="step-card">
            <div className="step-number">2</div>
            <h3>Browse or Submit Notes</h3>
            <p>Search published notes, download past exam papers, or submit your own class notes to your department faculty for review.</p>
          </div>

          <div className="step-card">
            <div className="step-number">3</div>
            <h3>Summarize & Study</h3>
            <p>Use the AI Notes Summarizer to generate instant revision sheets and key takeaways right before exams.</p>
          </div>
        </div>
      </section>

      {/* Roles Section */}
      <section id="roles" className="landing-section">
        <div className="section-title-group">
          <h2>Designed for Everyone on Campus</h2>
          <p>Clear role permissions tailored for students, faculty teachers, and administrators.</p>
        </div>

        <div className="role-grid">
          <div className="role-card">
            <div className="role-card-header">
              <div className="role-badge-icon" style={{ background: 'var(--success-bg)', color: 'var(--success)' }}>
                <FiUser />
              </div>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Students</h3>
            </div>
            <ul>
              <li><FiCheckCircle /> Search and download course notes</li>
              <li><FiCheckCircle /> Access past exam papers (Midsem & Endsem)</li>
              <li><FiCheckCircle /> Submit note requests for teacher approval</li>
              <li><FiCheckCircle /> Generate AI document summaries</li>
            </ul>
          </div>

          <div className="role-card">
            <div className="role-card-header">
              <div className="role-badge-icon" style={{ background: 'var(--primary-light)', color: 'var(--primary)' }}>
                <FiFileText />
              </div>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Teachers & Faculty</h3>
            </div>
            <ul>
              <li><FiCheckCircle /> Upload notes & papers directly</li>
              <li><FiCheckCircle /> Review & approve student note requests</li>
              <li><FiCheckCircle /> Reject requests with constructive feedback</li>
              <li><FiCheckCircle /> Delete outdated study materials</li>
            </ul>
          </div>

          <div className="role-card">
            <div className="role-card-header">
              <div className="role-badge-icon" style={{ background: 'var(--warning-bg)', color: 'var(--warning)' }}>
                <FiHome />
              </div>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>System Admins</h3>
            </div>
            <ul>
              <li><FiCheckCircle /> Provision institutions & email domains</li>
              <li><FiCheckCircle /> Add faculty accounts to colleges</li>
              <li><FiCheckCircle /> Manage & moderate user accounts</li>
              <li><FiCheckCircle /> View global platform analytics</li>
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="landing-section">
        <div className="section-title-group">
          <h2>Frequently Asked Questions</h2>
          <p>Everything you need to know about getting started on Notebook.</p>
        </div>

        <div className="faq-list">
          {faqs.map((faq, idx) => (
            <div key={idx} className="faq-item">
              <button
                className="faq-question"
                onClick={() => toggleFaq(idx)}
              >
                <span>{faq.q}</span>
                {openFaq === idx ? <FiChevronUp /> : <FiChevronDown />}
              </button>
              {openFaq === idx && (
                <div className="faq-answer">
                  {faq.a}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* CTA Banner */}
        <div className="landing-cta-banner">
          <h2>Ready to Elevate Your College Learning?</h2>
          <p>Join students and faculty across institutions studying smarter with Notebook.</p>
          <div className="d-flex justify-between align-center gap-3" style={{ justifyContent: 'center' }}>
            <button
              className="btn"
              style={{ backgroundColor: '#FFFFFF', color: 'var(--primary)', fontWeight: '600' }}
              onClick={() => navigate('/login?mode=register')}
            >
              Create Free Account <FiArrowRight />
            </button>
            <button
              className="btn btn-outline"
              style={{ color: '#FFFFFF', borderColor: 'rgba(255,255,255,0.4)' }}
              onClick={() => navigate('/login?mode=login')}
            >
              Sign In
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
